import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2, Users, Merge } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatNumber } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type {
  ContactListDto,
  ContactListMergeReportDto,
  ContactListRequestDto,
} from '@kardume/types';
import {
  Badge,
  Button,
  ActionButton,
  Checkbox,
  Modal,
  TextInput,
  Switch,
  Table,
  TableContainer,
} from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';
import ContactListDrawer from './ContactListDrawer';

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SelectionHint = styled.span`
  margin-right: auto;
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
`;

const SourceList = styled.ul`
  margin: 0.75rem 0 0;
  padding-left: 1.2rem;
  font-size: 0.85rem;
  line-height: 1.7;
  color: ${(p) => p.theme.textSecondary};
`;

const ReportSummary = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
  padding: 0.75rem 0.9rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.82rem;
  font-weight: 700;
  color: ${(p) => p.theme.textSecondary};
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const EMPTY_LIST_FORM: ContactListRequestDto = { name: '', description: '', active: true };

interface ContactListsTabProps {
  canManage: boolean;
}

/**
 * Listas de prospecção do time comercial.
 *
 * Aqui só a listagem: os contatos de uma lista abrem num drawer, para o operador
 * conferir e voltar sem perder a página em que estava.
 */
const ContactListsTab: React.FC<ContactListsTabProps> = ({ canManage }) => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listContactLists(), []);

  const [selected, setSelected] = useState<ContactListDto | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [listForm, setListForm] = useState<ContactListRequestDto>(EMPTY_LIST_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<ContactListDto | null>(null);
  const [removing, setRemoving] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [mergeForm, setMergeForm] = useState({ name: '', description: '' });
  const [merging, setMerging] = useState(false);
  const [mergeReport, setMergeReport] = useState<ContactListMergeReportDto | null>(null);

  const lists = data ?? [];
  // A ordem da seleção é a ordem em que as duplicatas são resolvidas no backend
  // (o primeiro visto é o que fica), então respeitá-la aqui é o que faz a prévia
  // da tela bater com o relatório que volta.
  const selectedLists = selectedIds
    .map((id) => lists.find((list) => list.id === id))
    .filter((list): list is ContactListDto => list !== undefined);
  const canMerge = selectedLists.length >= 2;

  const toggleSelection = (id: string): void =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));

  const openMerge = (): void => {
    setMergeForm({ name: '', description: '' });
    setMergeReport(null);
    setIsMergeOpen(true);
  };

  const submitMerge = async (): Promise<void> => {
    if (!canMerge) return;
    setMerging(true);
    try {
      const report = await backofficeApi.mergeContactLists({
        name: mergeForm.name.trim(),
        description: mergeForm.description.trim() || undefined,
        sourceListIds: selectedLists.map((list) => list.id),
      });
      setMergeReport(report);
      showNotification(
        `Lista "${report.list.name}" criada com ${formatNumber(report.imported)} contato(s).`,
        'success'
      );
      setSelectedIds([]);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível mesclar as listas.'), 'error');
    } finally {
      setMerging(false);
    }
  };

  const openCreate = (): void => {
    setEditingId(null);
    setListForm(EMPTY_LIST_FORM);
    setIsFormOpen(true);
  };

  const openEdit = (list: ContactListDto): void => {
    setEditingId(list.id);
    setListForm({ name: list.name, description: list.description ?? '', active: list.active });
    setIsFormOpen(true);
  };

  const submitList = async (): Promise<void> => {
    setSubmitting(true);
    try {
      if (editingId) {
        await backofficeApi.updateContactList(editingId, listForm);
        showNotification('Lista atualizada.', 'success');
      } else {
        await backofficeApi.createContactList(listForm);
        showNotification('Lista criada.', 'success');
      }
      setIsFormOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar a lista.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteContactList(deleting.id);
      showNotification('Lista removida.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover a lista.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      {canManage && (
        <Toolbar>
          {selectedIds.length > 0 && (
            <SelectionHint>
              {formatNumber(selectedIds.length)} lista(s) marcada(s)
              {canMerge ? '' : ' - marque pelo menos duas para mesclar'}
            </SelectionHint>
          )}
          <Button variant="outline" onClick={openMerge} disabled={!canMerge}>
            <Merge size={16} /> Mesclar
          </Button>
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova lista
          </Button>
        </Toolbar>
      )}

      <DataState
        loading={loading && !data}
        error={error}
        isEmpty={lists.length === 0}
        emptyTitle="Nenhuma lista ainda"
        emptyDescription="Crie uma lista para o time comercial cadastrar os lojistas a contatar."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                {canManage && <th style={{ width: '1%' }} aria-label="Selecionar" />}
                <th>Lista</th>
                <th style={{ whiteSpace: 'nowrap' }}>Contatos</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lists.map((list) => (
                <tr key={list.id}>
                  {canManage && (
                    <td>
                      <Checkbox
                        checked={selectedIds.includes(list.id)}
                        onChange={() => toggleSelection(list.id)}
                        aria-label={`Selecionar ${list.name}`}
                      />
                    </td>
                  )}
                  <td>
                    <strong>{list.name}</strong>
                    {list.description && <FieldHint>{list.description}</FieldHint>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatNumber(list.membersCount)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={list.active ? 'success' : 'secondary'}>
                      {list.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      <ActionButton
                        title="Ver contatos"
                        aria-label="Ver contatos"
                        onClick={() => setSelected(list)}
                      >
                        <Users size={16} />
                      </ActionButton>
                      {canManage && (
                        <>
                          <ActionButton title="Editar" aria-label="Editar lista" onClick={() => openEdit(list)}>
                            <Edit3 size={16} />
                          </ActionButton>
                          <ActionButton
                            title="Remover"
                            aria-label="Remover lista"
                            color="#DC2626"
                            onClick={() => setDeleting(list)}
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <ContactListDrawer
        contactList={selected}
        canManage={canManage}
        onClose={() => setSelected(null)}
        onChanged={reload}
      />

      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingId ? 'Editar lista' : 'Nova lista'}
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsFormOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submitList} isLoading={submitting} disabled={!listForm.name}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome da lista"
          value={listForm.name}
          onChange={(e) => setListForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Ex.: Lojistas Toledo - outubro"
        />
        <TextInput
          label="Descrição (opcional)"
          value={listForm.description ?? ''}
          onChange={(e) => setListForm((prev) => ({ ...prev, description: e.target.value }))}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <ToggleField>
          <Switch
            checked={listForm.active}
            onChange={(v) => setListForm((prev) => ({ ...prev, active: v }))}
          />
          Lista ativa
        </ToggleField>
      </Modal>

      <Modal
        isOpen={isMergeOpen}
        onClose={() => setIsMergeOpen(false)}
        title="Mesclar listas"
        width="520px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsMergeOpen(false)} disabled={merging}>
              {mergeReport ? 'Fechar' : 'Cancelar'}
            </Button>
            {!mergeReport && (
              <Button
                variant="primary"
                onClick={submitMerge}
                isLoading={merging}
                disabled={!mergeForm.name.trim() || !canMerge}
              >
                Mesclar {formatNumber(selectedLists.length)} listas
              </Button>
            )}
          </>
        }
      >
        {mergeReport ? (
          <>
            <ConfirmText>
              <strong>{mergeReport.list.name}</strong> criada com a união dos números. As listas de
              origem foram desativadas - campanhas antigas continuam apontando para elas.
            </ConfirmText>
            <ReportSummary>
              <span>Recebidos: {formatNumber(mergeReport.received)}</span>
              <span>Importados: {formatNumber(mergeReport.imported)}</span>
              <span>Duplicados: {formatNumber(mergeReport.duplicated)}</span>
            </ReportSummary>
          </>
        ) : (
          <>
            <TextInput
              label="Nome da lista nova"
              value={mergeForm.name}
              onChange={(e) => setMergeForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Ex.: Lojistas Toledo - consolidada"
            />
            <TextInput
              label="Descrição (opcional)"
              value={mergeForm.description}
              onChange={(e) => setMergeForm((prev) => ({ ...prev, description: e.target.value }))}
              containerStyle={{ marginTop: '0.75rem' }}
            />
            <SourceList>
              {selectedLists.map((list) => (
                <li key={list.id}>
                  {list.name} · {formatNumber(list.membersCount)} contato(s)
                </li>
              ))}
            </SourceList>
            <FieldHint>
              A lista nova recebe a união dos números, sem repetir: quando o mesmo número está em
              mais de uma origem, vale o registro da primeira marcada. As origens acima são{' '}
              <strong>desativadas</strong>, não apagadas - o histórico das campanhas antigas
              continua legível.
            </FieldHint>
          </>
        )}
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remover lista"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover <strong>{deleting?.name}</strong> e seus {formatNumber(deleting?.membersCount ?? 0)}{' '}
          contato(s)? Listas usadas por alguma campanha não podem ser removidas - desative-as em vez
          disso.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default ContactListsTab;
